import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { BluLoader } from '@bludata/components';
import axios from 'axios';
import { currentYear, getVersioneFocus } from './CrmFooter';
import { useEffect, useState } from 'react';
import moment from 'moment';
import useBludataLogoThemed from '../../utility_objects/const/imageUrls';
import pluginCodes, {
  pluginDesc,
} from '../../../../../packages/utility-functions/src/lib/pluginCodes';
const HomeIcon = '/resources/images/Home-4--Streamline-Core.svg';

interface Props {
  onClose: () => void;
}

interface VersioniFocusInterface {
  BluServiceCRM?: string;
  FocusWeb?: string;
  FocusWebServer?: string;
  ServiceGatewayClient?: string;
}

/**
 * Compara due versioni e restituisce un booleano che indica se la versione1 è maggiore della versione2.
 *
 * @param version1 - La prima versione da confrontare.
 * @param version2 - La seconda versione da confrontare.
 * @returns Un booleano che indica se la versione1 è maggiore della versione2.
 */
export function compareVersions(version1: string, version2: string): boolean {
  if (!version1 || !version2) {
    return false; // Se una delle due versioni è una stringa vuota o un valore non definito, ritorna sempre false
  }

  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);

  for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
    const v1 = v1Parts[i] || 0;
    const v2 = v2Parts[i] || 0;

    if (v1 > v2) return true;
    if (v1 < v2) return false;
  }

  return true; // Le versioni sono uguali
}

const urlVersioniServizi = 'https://focus.bludata.com/lastServicesVersion.json';

const FocusInfoDialog = (props: Props) => {
  const { logoFocusUrl } = useBludataLogoThemed();
  const { onClose } = props;
  const { loginData } = useSelector((state: RootState) => state.root);
  const [versioneCrm, setVersioneCrm] = useState('');
  const { Versione } = loginData;

  // @ts-expect-error - __BUILD_DATE__ è iniettato a build time in vite.config.mts
  const versionDate = __BUILD_DATE__ ? new Date(__BUILD_DATE__) : null;
  const momentDate = versionDate ? moment(versionDate) : null;
  const buildDateString = momentDate ? momentDate.format('DD/MM/YYYY') : '';
  const { Plugin } = loginData;
  // Normalizza i codici plugin (numeri o stringhe) in un Set di stringhe
  const pluginIdsSet = new Set(
    (Array.isArray(Plugin) ? Plugin : []).map((p) => String(p)),
  );
  const activePluginDescriptions = Object.entries(pluginCodes).reduce<string[]>(
    (acc, [nome, codice]) => {
      if (pluginIdsSet.has(String(codice))) {
        const descr = pluginDesc[nome as keyof typeof pluginDesc];
        acc.push(String(descr));
      }
      return acc;
    },
    [],
  );
  const midIndex = Math.ceil(activePluginDescriptions.length / 2);
  const col1 = activePluginDescriptions.slice(0, midIndex);
  const col2 = activePluginDescriptions.slice(midIndex);

  useEffect(() => {
    axios
      .get<{ valoreVersione: string }>('/crmapi/getVersioneCRM')
      .then((response) => {
        if (response.data && response.data.valoreVersione) {
          setVersioneCrm(response.data.valoreVersione);
        }
      });
  }, []);

  return (
    <Dialog open fullWidth maxWidth="sm" fullScreen={false}>
      <DialogContent sx={{ backgroundColor: 'var(--base-foreground-color)' }}>
        <Stack flexDirection="column" spacing={2} alignItems="center">
          <Box
            sx={{
              cursor: 'pointer',
              height: 40,
              width: 240,
              backgroundImage: `url(${logoFocusUrl})`,
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'auto 100%',
              backgroundPosition: 'center',
            }}
          />

          <Typography fontWeight="600">
            © {currentYear} Bludata S.r.l.
          </Typography>
          <Stack flexDirection="column" spacing={2} alignItems="center">
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                width: '500px',
                backgroundColor: 'var(--background-color)',
              }}
            >
              <Stack sx={{ ml: 1 }} flexDirection="column" spacing={1}>
                <Stack flexDirection="row" gap={3}>
                  <Stack
                    flexDirection="column"
                    justifyContent="start"
                    sx={{
                      '& path': {
                        stroke: 'var(--base-color)',
                      },
                    }}
                  >
                    <img
                      style={{ width: '20px', height: '20px' }}
                      src={HomeIcon}
                      alt=""
                    />
                    {/* <HomeIcon style={{ width: '20px', height: '20px' }} /> */}
                  </Stack>
                  <Stack flexDirection="column">
                    <Typography fontWeight="bold">Bludata S.r.l.</Typography>
                    <Typography>Via delle industrie, 10</Typography>
                    <Typography>31030 Breda di Piave (TV) - Italia</Typography>
                  </Stack>
                </Stack>

                <Stack flexDirection="row" gap={3}>
                  <Stack
                    flexDirection="column"
                    justifyContent="start"
                    sx={{
                      '& path': {
                        stroke: 'var(--base-color)',
                      },
                    }}
                  >
                    <img
                      style={{ width: '20px', height: '20px' }}
                      src={'resources/images/Phone--Streamline-Core.svg'}
                      alt=""
                    />
                    {/* <PhoneIcon style={{ width: '20px', height: '20px' }} /> */}
                  </Stack>
                  <Stack flexDirection="column">
                    <Typography fontWeight="bold">Contatti</Typography>
                    <Typography>Servizio assistenza</Typography>
                    <Typography>Uffici commerciali</Typography>
                    <Typography>Sito web</Typography>
                    <Typography>E-mail</Typography>
                  </Stack>
                  <Stack flexDirection="column" justifyContent="end">
                    <Typography>0422 906000</Typography>
                    <Typography>0422 445442</Typography>
                    <Typography>
                      <a
                        href="https://www.bludata.com"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: 'var(--base-color',
                          textTransform: 'none',
                        }}
                      >
                        https://www.bludata.com
                      </a>
                    </Typography>
                    <Typography>
                      <a
                        href="mailto:assistenza@bludata.com"
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: 'var(--base-color',
                          textTransform: 'none',
                        }}
                      >
                        assistenza@bludata.com
                      </a>
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>
            </Paper>
            {activePluginDescriptions.length > 0 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  width: '500px',
                  backgroundColor: 'var(--background-color)',
                }}
              >
                <Stack sx={{ ml: 1 }} flexDirection="column" spacing={0.5}>
                  <Typography fontWeight="bold">Plugin attivi</Typography>
                  {/* Sostituito spacing con gap per evitare offset verticale */}
                  <Stack flexDirection="row" gap={4} alignItems="flex-start">
                    <Stack spacing={0.25} flex={1}>
                      {col1.map((d, i) => (
                        <Typography key={i} variant="body2">
                          • {d}
                        </Typography>
                      ))}
                    </Stack>
                    <Stack spacing={0.25} flex={1}>
                      {col2.map((d, i) => (
                        <Typography key={i} variant="body2">
                          • {d}
                        </Typography>
                      ))}
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            )}
            <Paper
              variant="outlined"
              sx={{
                p: 1,
                width: '500px',
                backgroundColor: 'var(--background-color)',
              }}
            >
              <Stack sx={{ ml: 1 }} flexDirection="column" spacing={0.5}>
                <Typography fontWeight="bold">Informazioni versione</Typography>
                <Stack flexDirection="row" gap={1} flexWrap="wrap">
                  <Chip
                    label={`Versione: ${getVersioneFocus()}`}
                    size="small"
                    variant="outlined"
                  />
                  {Versione && (
                    <Chip
                      label={`Versione Server: ${Versione}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                  {buildDateString && (
                    <Chip
                      label={`Data pubblicazione FRONTEND: ${buildDateString}`}
                      size="small"
                      variant="outlined"
                    />
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ backgroundColor: 'var(--base-foreground-color)' }}>
        <Button variant="outlined" onClick={onClose}>
          Chiudi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FocusInfoDialog;
